<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { useBookExposure } from '@/composables/tracks/useBookExposure'
import { resources } from '@/config/brands'

const props = withDefaults(defineProps<{
  id: string // 唯一id，用于曝光
  src: string // 真实图片地址
  alt?: string // 图片alt
  placeholder?: string // 占位图地址
  isReportImp?: boolean // 是否曝光埋点
  extra?: Record<string, any> // 埋点扩展数据
  heightRate?: number
  ignorePicturesAppear?: boolean // 某些图片过大, 以object-cover显示时 intersectionRatio 永远不会大于0.5 导致无法曝光埋点, 配置这个直接忽略大于 0.5 的判断
  lazy?: boolean // 是否懒加载, 默认 true; swiper loop 等会克隆 DOM 的场景需置为 false, 否则克隆 slide 拿不到真实图片
}>(), {
  alt: '',
  placeholder: resources.bookCoverPlaceholder,
  isReportImp: true,
  heightRate: 133,
  ignorePicturesAppear: false,
  lazy: true,
})

const { bookExposure } = useBookExposure()
// 非懒加载时直接渲染真实图片, 保证 swiper loop 克隆 slide 能拿到带 src 的图片节点
const imgSrc = ref(props.lazy ? '' : props.src)
const hasExposed = ref(false)
const isIntersecting = ref(false)
const imgLoaded = ref(false)
const ob = ref<any>(null)

function onImgError() {
  imgSrc.value = props.placeholder
}

function onImgLoad() {
  imgLoaded.value = true
}

// 书籍身份变化时（如列表组件实例被复用、切换 tab）重置状态:
// 重新加载图片, 并重置曝光标记, 保证新书能再次触发 bookExposure
watch(() => [props.extra?.id, props.src], () => {
  imgLoaded.value = false
  hasExposed.value = false
  // 懒加载且当前不可见时先清空, 等进入视口再加载; 否则直接加载新图
  imgSrc.value = props.lazy && !isIntersecting.value ? '' : props.src
})

// 监听曝光与懒加载
onMounted(() => {
  ob.value = useIntersectionObserver(
    document.getElementById(props.id),
    ([{ isIntersecting: intersecting, intersectionRatio }]) => {
      isIntersecting.value = intersecting
      // 懒加载 - 可见度达到 10% 即开始加载
      if (intersecting && intersectionRatio >= 0.1 && !imgLoaded.value)
        imgSrc.value = props.src

      // 曝光埋点 - 可见度达到 50% 才触发曝光
      if (intersecting && props.isReportImp && !hasExposed.value && (props.ignorePicturesAppear || intersectionRatio >= 0.5)) {
        bookExposure({
          module_name: props.extra?.module_name || '',
          module_id: props.extra?.module_id || '',
          module_type: props.extra?.module_type || '',
          book_id: props.extra?.id || '',
          book_name: props.extra?.contentName || '',
          author: props.extra?.author || '',
        })
        hasExposed.value = true
      }
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: [0.1, 0.5], // 监听 10% 和 50% 两个阈值
    },
  )
})

onBeforeUnmount(() => {
  if (ob.value)
    ob.value.stop()
})
</script>

<template>
  <div class="overflow-hidden">
    <div
      :id="id"
      class="overflow-hidden w-full relative h-0"
      :style="{ paddingBottom: `${heightRate}%` }"
    >
      <img
        class="w-full left-0 absolute h-full object-cover top-0"
        :src="placeholder"
        :alt="alt"
      >

      <img
        v-if="imgSrc"
        class="w-full left-0 absolute h-full object-cover top-0 transition-opacity duration-300"
        :class="{ 'opacity-100': imgLoaded, 'opacity-0': !imgLoaded }"
        :src="imgSrc"
        :alt="alt"
        @load="onImgLoad"
        @error="onImgError"
      >
    </div>
  </div>
</template>
